// SAD-1000: Needs Attention, worded. The server ranks and counts the queue
// (ListAttentionItems); this module says, for each item, what it is, where
// it is, how urgent, and which existing surface its verb opens. Every verb is
// a link to the page that already does it, except Remind (SAD-1000), which
// a late or at-risk date offers beside Open.
import { dueWords, listHrefFor } from './listView';
/** The tabs, in the server's consequence order, with "All" first. */ export const ATTENTION_TABS = [
    {
        value: 'all',
        label: 'All'
    },
    {
        value: 'late',
        label: 'Late'
    },
    {
        value: 'waiting',
        label: 'Waiting on approval'
    },
    {
        value: 'request',
        label: 'Requests'
    },
    {
        value: 'at_risk',
        label: 'At risk'
    },
    {
        value: 'review',
        label: 'Review items'
    }
];
/** How many rows a tab shows before "Show more". */ export const ATTENTION_CAP = 6;
const plural = (n, one, many = `${one}s`)=>`${n.toLocaleString()} ${n === 1 ? one : many}`;
const REQUEST_WORD = {
    waiver: 'Waiver',
    not_owed: 'Not owed',
    extension: 'Extension',
    submission_edit: 'Filing edit',
    submission_evidence: 'Evidence',
    waiver_evidence: 'Waiver evidence',
    extension_evidence: 'Extension evidence'
};
const REVIEW_TYPE = {
    Modification: 'Modification',
    ReExtraction: 'Re-extraction',
    ContractIntake: 'Contract intake'
};
export function badgeOf(it) {
    switch(it.kind){
        case 'late':
            return {
                label: `Late ${it.ageDays.toLocaleString()}d`,
                tone: 'danger'
            };
        case 'waiting':
            return {
                label: `Waiting ${it.ageDays.toLocaleString()}d`,
                tone: 'orange'
            };
        case 'request':
            return {
                label: REQUEST_WORD[it.requestKind] ?? 'Request',
                tone: 'warning'
            };
        case 'at_risk':
            return {
                label: 'At risk',
                tone: 'warning'
            };
        default:
            return {
                label: `Review ${it.openReviewCount.toLocaleString()}`,
                tone: 'info'
            };
    }
}
const day = (iso)=>iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
    }) : '';
/** The row's first line: what needs doing, in plain words. */ export function whatOf(it) {
    const name = [
        it.title || 'Deliverable',
        it.label
    ].filter(Boolean).join(' — ');
    switch(it.kind){
        case 'request':
            switch(it.requestKind){
                case 'extension':
                    return `${name}: extension to ${day(it.requestedDueOn)} requested`;
                case 'waiver':
                    return `${name}: waiver requested`;
                case 'not_owed':
                    return `${name}: proposed as not owed`;
                case 'submission_edit':
                    return `${name}: a correction to its filing proposed`;
                case 'waiver_evidence':
                    return `${name}: evidence added to its waiver, to approve`;
                case 'extension_evidence':
                    return `${name}: evidence added to its extension, to approve`;
                default:
                    return `${name}: evidence added to its filing, to approve`;
            }
        case 'review':
            {
                const what = it.title || REVIEW_TYPE[it.changeSetType] || 'Change set';
                return it.changeSetType === 'ContractIntake' ? `${what}: ${plural(it.openReviewCount, 'question')} to answer` : `${what}: ${plural(it.openReviewCount, 'change')} awaiting a decision`;
            }
        default:
            return name;
    }
}
/** The row's second line: where it is and whose it is. */ export function whereOf(it) {
    if (it.kind === 'review') {
        return [
            it.programName,
            REVIEW_TYPE[it.changeSetType]
        ].filter(Boolean).join(' · ');
    }
    if (it.kind === 'request') {
        return [
            it.programName,
            it.workstreamName,
            it.requestedBy && `requested by ${it.requestedBy}`
        ].filter(Boolean).join(' · ');
    }
    return [
        it.programName,
        it.workstreamName || 'Contract-wide',
        it.ownerName || 'No owner'
    ].filter(Boolean).join(' · ');
}
/** The urgency: the date that matters, and how far it is. */ export function whenOf(it, today) {
    switch(it.kind){
        case 'late':
        case 'at_risk':
            {
                const d = dueWords(it.dueOn, today);
                return {
                    date: `Due ${d.date}`,
                    rel: d.rel
                };
            }
        case 'waiting':
            return {
                date: `Filed ${day(it.since)}`,
                rel: `waiting ${plural(it.ageDays, 'day')}`
            };
        case 'request':
            return {
                date: `Requested ${day(it.since)}`,
                rel: it.ageDays > 0 ? `${plural(it.ageDays, 'day')} ago` : 'today'
            };
        default:
            return {
                date: `Opened ${day(it.since)}`,
                rel: it.ageDays > 0 ? `${plural(it.ageDays, 'day')} ago` : 'today'
            };
    }
}
/**
 * The verb and where it goes, always an existing surface: a date's drawer
 * on its program (where it is filed, moved or excused), Record review over
 * it, or the Modification Center on the change set.
 */ export function verbOf(it) {
    const date = `/delivery/programs/${it.programId}/deliverables?occ=${encodeURIComponent(it.occurrenceId)}`;
    switch(it.kind){
        case 'waiting':
            return {
                label: 'Review',
                href: it.submissionId ? `${date}&review=${encodeURIComponent(it.submissionId)}` : date
            };
        case 'request':
            return {
                label: 'Decide',
                href: date
            };
        case 'review':
            return {
                label: 'Decide',
                href: `/delivery/programs/${it.programId}/docs?tab=modifications&set=${encodeURIComponent(it.changeSetId)}`
            };
        default:
            return {
                label: 'Open',
                href: date
            };
    }
}
/**
 * Where a tab's every row is, when they are dates: the drill-down list under
 * the Command Center's filter. Requests and review items are no list of
 * dates; they show in full in place.
 */ export function tabListHref(tab, filter) {
    const states = {
        all: [
            'late',
            'at_risk',
            'awaiting_review'
        ],
        late: [
            'late'
        ],
        at_risk: [
            'at_risk'
        ],
        waiting: [
            'awaiting_review'
        ]
    };
    return states[tab] ? listHrefFor({
        ...filter,
        states: states[tab]
    }) : '';
}
/** A date can be reminded once a day (the server holds it to that). */ export const REMIND_INTERVAL_MS = 24 * 3600000;
/** The date states that still owe a filing, so can be reminded. */ const REMINDABLE = new Set([
    'late',
    'due_soon',
    'upcoming',
    'rejected'
]);
export const remindableState = (state)=>REMINDABLE.has(state ?? '');
export const NO_OWNER_TO_REMIND = 'No one owns this date. Give its team an owner or its program a lead to remind them.';
/**
 * "Reminded 3h ago" while a reminder is under a day old, else "" (Remind
 * is offered again).
 */ export function remindedWords(at, now = Date.now()) {
    if (!at) return '';
    const ms = now - new Date(at).getTime();
    if (Number.isNaN(ms) || ms >= REMIND_INTERVAL_MS) return '';
    if (ms < 60000) return 'Reminded just now';
    if (ms < 3600000) return `Reminded ${Math.floor(ms / 60000)}m ago`;
    return `Reminded ${Math.floor(ms / 3600000)}h ago`;
}
